import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Copy, Share2, IndianRupee, Users, Gift, CheckCircle2, Trophy } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const ReferAndEarn = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    publicApi.get("/user/referral")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load referral info"))
      .finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(data.share_url);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2500);
  };

  const shareWhatsApp = () => {
    const text = `Hey! I recently travelled with Andaman Avalon and loved it. Use my code *${data.code}* to get ${data.discount_pct}% off your booking — ${data.share_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="refer-page" className="grain-texture min-h-screen">
      <SEO title="Refer & Earn ₹1,000" description="Share Andaman Avalon with friends. They get 5% off, you earn ₹1,000 credit on every booking." path="/refer" noindex />
      <Navbar />

      <section className="pt-32 pb-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">EXCLUSIVE FOR YOU</span>
          <h1 className="text-4xl sm:text-5xl font-serif mt-3 text-white">Refer &amp; Earn</h1>

          {/* Tier badge + progress */}
          {data.tier && (
            <div className="mt-6 max-w-md mx-auto" data-testid="tier-badge-block">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold"
                   style={{ background: data.tier.badge_color + '20', color: data.tier.badge_color, border: `1.5px solid ${data.tier.badge_color}` }}
                   data-testid="tier-badge">
                <Trophy size={16} /> {data.tier.tier} member
              </div>
              {data.tier.next_tier && (
                <div className="mt-4">
                  <div className="text-xs text-white/70 mb-2 flex justify-between">
                    <span>{data.tier.referrals_credited} / {data.tier.next_threshold} referrals</span>
                    <span>{data.tier.next_threshold - data.tier.referrals_credited} more to reach <b>{data.tier.next_tier}</b></span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all"
                      style={{ width: `${Math.min(100, (data.tier.referrals_credited / data.tier.next_threshold) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-white/80 mt-6 max-w-2xl mx-auto">
            Share your unique code — friends get <strong className="text-secondary">{data.discount_pct}% off</strong>, you get <strong className="text-secondary">₹{data.reward_inr.toLocaleString('en-IN')} credit</strong> when they book.
          </p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {/* Stats grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="referral-stats">
            <StatCard icon={IndianRupee} label="Your credit balance" value={`₹${Number(data.credit_balance).toLocaleString('en-IN')}`} accent testid="stat-balance" />
            <StatCard icon={Users} label="Friends referred" value={String(data.total_referrals)} testid="stat-referrals" />
            <StatCard icon={Gift} label="Total earned" value={`₹${Number(data.total_earned_inr).toLocaleString('en-IN')}`} testid="stat-earned" />
          </div>

          {/* Referral code + share block */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="admin-card">
            <h2 className="text-2xl font-serif text-primary mb-4">Your referral code</h2>
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <div className="flex-1 bg-muted/50 rounded-lg p-4 text-center">
                <code className="text-3xl font-mono font-bold tracking-widest text-primary" data-testid="referral-code-display">
                  {data.code}
                </code>
              </div>
              <div className="flex flex-col gap-2 sm:w-56">
                <Button
                  onClick={copyLink}
                  data-testid="copy-referral-btn"
                  className="bg-primary hover:bg-primary/90 h-11 gap-2"
                >
                  {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  {copied ? "Copied!" : "Copy link"}
                </Button>
                <Button
                  onClick={shareWhatsApp}
                  data-testid="share-whatsapp-btn"
                  variant="outline"
                  className="h-11 gap-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"
                >
                  <Share2 size={16} /> Share on WhatsApp
                </Button>
              </div>
            </div>
            <div className="mt-4 text-sm text-muted-foreground break-all">
              <span className="font-medium text-foreground">Your link:</span> {data.share_url}
            </div>
          </motion.div>

          {/* How it works */}
          <div className="admin-card">
            <h2 className="text-2xl font-serif text-primary mb-4">How it works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Step number={1} title="Share your code" desc="Send your unique code or link to any friend planning an Andaman trip." />
              <Step number={2} title="They save 5%" desc={`Your friend enters your code at checkout and instantly gets ${data.discount_pct}% off any package.`} />
              <Step number={3} title="You earn ₹1,000" desc={`The moment their payment goes through, ₹${data.reward_inr.toLocaleString('en-IN')} is credited to your account for your next trip.`} />
            </div>
          </div>

          {/* Tier ladder */}
          {data.all_tiers && (
            <div className="admin-card" data-testid="tier-ladder">
              <h2 className="text-2xl font-serif text-primary mb-4">Loyalty tiers</h2>
              <p className="text-sm text-muted-foreground mb-5">The more friends you refer, the bigger your reward — automatically.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {data.all_tiers.map((t) => {
                  const isCurrent = data.tier && t.name === data.tier.tier;
                  return (
                    <div
                      key={t.name}
                      data-testid={`tier-card-${t.name.toLowerCase()}`}
                      className={`rounded-lg p-5 border-2 ${isCurrent ? "border-secondary bg-secondary/5 shadow-lg" : "border-border/60"}`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Trophy size={20} style={{ color: t.badge_color }} />
                        <span className="font-semibold" style={{ color: t.badge_color }}>{t.name}</span>
                        {isCurrent && <span className="ml-auto text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">YOU</span>}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        {t.min_referrals === 0 ? "Everyone" : `${t.min_referrals}+ referrals`}
                      </div>
                      <div className="mt-3 space-y-1 text-sm">
                        <div>Friend gets <strong>{t.discount_pct}% off</strong></div>
                        <div>You earn <strong>₹{t.reward_inr.toLocaleString('en-IN')}</strong> per referral</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent referrals list */}
          <div className="admin-card">
            <h2 className="text-2xl font-serif text-primary mb-4">Your referrals</h2>
            {data.recent.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="no-referrals">
                No referrals yet. Share your code above to start earning!
              </p>
            ) : (
              <ul className="divide-y" data-testid="referral-history">
                {data.recent.map((r) => (
                  <li key={r.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{r.referred_customer_name || r.referred_customer_email}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.package_name} · {new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-green-700">+ ₹{Number(r.reward_amount).toLocaleString('en-IN')}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Terms note */}
          <p className="text-xs text-muted-foreground text-center">
            Credit is only earned after your friend completes payment. You cannot use your own code. Credits apply to future bookings only; no cash-out.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className={`admin-card text-center ${accent ? 'ring-2 ring-secondary' : ''}`}>
    <Icon className={`mx-auto mb-2 ${accent ? 'text-secondary' : 'text-primary'}`} size={24} />
    <div className="text-2xl font-bold text-primary">{value}</div>
    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
  </div>
);

const Step = ({ number, title, desc }) => (
  <div>
    <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold mb-3">
      {number}
    </div>
    <h3 className="font-semibold text-primary mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground">{desc}</p>
  </div>
);

export default ReferAndEarn;
